import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Input } from '@heroui/react'
import { api, ApiError } from '../lib/api'
import type { KerberosDraft, KerberosSettings } from '../lib/types'
import { Icon } from '../ui/icons'
import { StatusChip } from '../ui/parts'
import { TestResult } from './connectParts'
import { Toggle } from './sectionKit'
import { InnerPage, Notice, SettingBlock, SettingRow, SettingsCard, TagList, Values } from './settingsKit'

/**
 * Windows sign-in (Kerberos through HTTP Negotiate): a card on the Single sign-on
 * page and its edit page. The installation owner sets it (in the cloud, the
 * organisation's administrator too); the keytab is uploaded,
 * sealed on the server and never shown again.
 */

export const WINDOWS = 'windows'

const BLANK: KerberosDraft = {
  servicePrincipal: '',
  realm: '',
  allowedRealms: [],
  groupsFrom: 'directory',
  enabled: false,
  tryAutomatically: false,
}

const refusal = (err: unknown) => (err instanceof ApiError ? err.message : 'The server could not be reached.')

/** The file's bytes as base64, as the server takes a keytab. */
async function base64Of(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s)
}

export function useKerberos(organisation?: string) {
  const [saved, setSaved] = useState<KerberosSettings | null>(null)
  const [error, setError] = useState('')
  const load = useCallback(async () => {
    try {
      setSaved((await api.kerberos(organisation)).kerberos)
      setError('')
    } catch (err) {
      setError(refusal(err))
    }
  }, [organisation])
  useEffect(() => {
    void load()
  }, [load])
  return { saved, error, load }
}

export function WindowsSignInCard({
  organisation,
  saved,
  error,
  canEdit,
  onEdit,
}: {
  organisation?: string
  saved: KerberosSettings | null
  error: string
  canEdit: boolean
  onEdit: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [tested, setTested] = useState<{ ok: boolean; text: string } | null>(null)

  async function test() {
    setBusy(true)
    setTested(null)
    try {
      const r = await api.testKerberos(organisation)
      setTested({ ok: true, text: `${r.servicePrincipal} · version ${r.kvno}` })
    } catch (err) {
      setTested({ ok: false, text: refusal(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <SettingsCard
      title="Windows sign-in"
      aside={
        <>
          {canEdit && (
            <Button variant="secondary" size="sm" onPress={onEdit}>
              {saved ? (
                <>
                  <Icon name="edit" size={14} /> Edit
                </>
              ) : (
                'Set up'
              )}
            </Button>
          )}
          <StatusChip tone={saved?.enabled ? 'ok' : undefined}>{!saved ? 'Not set up' : saved.enabled ? 'On' : 'Off'}</StatusChip>
        </>
      }
    >
      {error && (
        <SettingBlock>
          <Notice>{error}</Notice>
        </SettingBlock>
      )}
      {saved?.keytabUnreadable && (
        <SettingBlock>
          <Notice tone="warning">Keytab unreadable on this server. Upload it again.</Notice>
        </SettingBlock>
      )}
      {saved && (
        <>
          <SettingRow label="Service">
            <Values
              items={[
                { label: 'Principal', value: <span className="connect-mono">{saved.servicePrincipal}</span> },
                { label: 'Realms', value: <span className="connect-mono">{[saved.realm, ...saved.allowedRealms].join(', ')}</span> },
                { label: 'Groups', value: saved.groupsFrom === 'directory' ? 'From the directory' : <span className="set-muted">Roles set by hand</span> },
                { label: 'Try automatically', value: saved.tryAutomatically ? 'On' : 'Off' },
              ]}
            />
          </SettingRow>
          <SettingRow label="Keytab">
            {saved.keytabSet ? (
              <Values
                items={saved.keys.map((k) => ({
                  key: `${k.principal}#${k.kvno}`,
                  label: `Version ${k.kvno}`,
                  value: (
                    <span className="connect-mono">
                      {k.principal} · {k.encryptionTypes.join(', ')}
                    </span>
                  ),
                }))}
              />
            ) : (
              <span className="set-muted">None</span>
            )}
          </SettingRow>
          {canEdit && saved.keytabSet && (
            <SettingRow label="Test" description="Checks the keytab.">
              <Button variant="secondary" isDisabled={busy} onPress={() => void test()}>
                {busy ? 'Testing…' : 'Test'}
              </Button>
            </SettingRow>
          )}
          {tested && (
            <SettingBlock>
              <TestResult ok={tested.ok} title={tested.ok ? 'Keytab ready' : 'Not usable'}>
                <span className={tested.ok ? 'connect-mono' : undefined}>{tested.text}</span>
              </TestResult>
            </SettingBlock>
          )}
        </>
      )}
    </SettingsCard>
  )
}

export function WindowsSignInPage({
  organisation,
  saved,
  back,
  onClose,
  onSaved,
}: {
  organisation?: string
  saved: KerberosSettings | null
  back: string
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [draft, setDraft] = useState<KerberosDraft>(
    saved
      ? {
          servicePrincipal: saved.servicePrincipal,
          realm: saved.realm,
          allowedRealms: saved.allowedRealms,
          groupsFrom: saved.groupsFrom,
          enabled: saved.enabled,
          tryAutomatically: saved.tryAutomatically === true,
        }
      : BLANK,
  )
  const [keytab, setKeytab] = useState<{ name: string; data: string } | null>(null)
  const [realm, setRealm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const picker = useRef<HTMLInputElement>(null)
  const set = (patch: Partial<KerberosDraft>) => setDraft((d) => ({ ...d, ...patch }))
  const valid = draft.servicePrincipal.trim() !== '' && draft.realm.trim() !== '' && (!draft.enabled || Boolean(keytab || saved?.keytabSet))

  const addRealm = () => {
    const typed = realm.toUpperCase().split(/[\s,]+/).filter(Boolean)
    set({ allowedRealms: [...draft.allowedRealms, ...typed.filter((r) => !draft.allowedRealms.includes(r))] })
    setRealm('')
  }

  async function save() {
    if (busy || !valid) return
    setBusy(true)
    setError('')
    try {
      await api.saveKerberos({
        organisation,
        ...draft,
        servicePrincipal: draft.servicePrincipal.trim(),
        realm: draft.realm.trim(),
        // Absent keeps the stored keytab.
        ...(keytab ? { keytab: keytab.data } : {}),
      })
      await onSaved()
    } catch (err) {
      setError(refusal(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage back={back} title="Windows sign-in" onClose={onClose} onSubmit={() => void save()} submitLabel="Save" valid={valid} busy={busy}>
      {error && <Notice>{error}</Notice>}
      <Toggle title="Windows sign-in" checked={draft.enabled} onChange={(enabled) => set({ enabled })} />
      <Toggle
        title="Try automatically"
        hint="Turn on after IT pushes the browser policy."
        checked={draft.tryAutomatically}
        onChange={(tryAutomatically) => set({ tryAutomatically })}
      />

      <section className="connect-part">
        <h3 className="connect-part__title">Service</h3>
        <label className="field">
          <span>Realm</span>
          <Input fullWidth className="connect-mono" value={draft.realm} onChange={(e) => set({ realm: e.target.value.toUpperCase() })} placeholder="BANK.LOCAL" />
        </label>
        <label className="field">
          <span>Service principal</span>
          <Input
            fullWidth
            className="connect-mono"
            value={draft.servicePrincipal}
            onChange={(e) => set({ servicePrincipal: e.target.value })}
            placeholder="HTTP/qms.bank.local"
          />
        </label>
        <div className="field">
          <span>Keytab</span>
          <input
            ref={picker}
            type="file"
            accept=".keytab,application/octet-stream"
            className="sr"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const f = e.target.files?.[0]
              e.target.value = ''
              if (f) void base64Of(f).then((data) => setKeytab({ name: f.name, data }))
            }}
          />
          <div className="connect-chip-add">
            <Button variant="secondary" onPress={() => picker.current?.click()}>
              <Icon name="upload" size={14} /> {keytab || saved?.keytabSet ? 'Replace' : 'Upload'}
            </Button>
            <span className="set-muted">{keytab ? keytab.name : saved?.keytabSet ? 'Stored' : 'None'}</span>
          </div>
        </div>
      </section>

      <section className="connect-part">
        <h3 className="connect-part__title">Who may sign in</h3>
        <Toggle
          title="Groups from the directory"
          hint="Off: roles set by hand, existing accounts only."
          checked={draft.groupsFrom === 'directory'}
          onChange={(on) => set({ groupsFrom: on ? 'directory' : 'none' })}
        />
        <TagList
          label="Other realms"
          items={draft.allowedRealms.map((r) => ({ id: r, label: r }))}
          onRemove={(r) => set({ allowedRealms: draft.allowedRealms.filter((x) => x !== r) })}
        />
        <div className="connect-chip-add">
          <Input
            value={realm}
            onChange={(e) => setRealm(e.target.value)}
            placeholder="BRANCH.BANK.LOCAL"
            aria-label="Add a realm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                addRealm()
              }
            }}
          />
          <Button variant="secondary" isDisabled={!realm.trim()} onPress={addRealm}>
            <Icon name="plus" size={14} /> Add
          </Button>
        </div>
      </section>
    </InnerPage>
  )
}
